import React, { useEffect, useState } from "react";
import {
  bulkread,
  conversations,
  getMessagesFilterCounts,
  unsuspenduser,
} from "../modules/MessageManager";
import { closeSpinner, modal, showSpinner } from "../modules/Dialogs";
import moment from "moment-timezone";
import { defaultMessagesFilter, userType } from "../config/GeneralConfig";
import { toScreen } from "../modules/Routing";
import { useNavigate, useNavigationType } from "react-router-dom";
import Pagination from "./Pagination";
import SuspensionModal from "./SuspensionModal";

const Messages = (props) => {
  const { userDetails } = props;
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const [restoreState] = useState(() => {
    const isBackFlag = sessionStorage.getItem("isBackNavigation") === "true";
    sessionStorage.removeItem("isBackNavigation");
    // Restore when: app Back button clicked (isBackFlag) OR browser back/forward (POP)
    const shouldRestore = isBackFlag || navigationType === "POP";
    if (shouldRestore) {
      const stored = sessionStorage.getItem("messagesRestoreState");
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return null;
        }
      }
    } else {
      sessionStorage.removeItem("messagesRestoreState");
    }
    return null;
  });

  const [messagesData, setMessagesData] = useState([]);
  const [filter, setFilter] = useState(
    restoreState?.selectedFilter ?? defaultMessagesFilter
  );
  const [searchValue, setSearchValue] = useState("");
  const [pageSize, setPageSize] = useState(restoreState?.selectedPageSize ?? 15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(restoreState?.selectedPage ?? 1);
  const [inputValue, setInputValue] = useState(restoreState?.selectedPage ?? 1);
  const [selectedCheckbox, setSelectedCheckbox] = useState([]);
  const [filterCount, setFilterCount] = useState({
    unread: null,
    disputes: null,
    flagged: null,
    flaggedByAI: null,
  });
  const [suspensionModalData, setSuspensionModalData] = useState(null);

  // Helper function to normalize message data access
  const getMessageData = (messageDetails) => {
    const messageData =
      messageDetails?.message || messageDetails?.messages || {};
    return messageData;
  };
  useEffect(() => {
    setInputValue(page);
  }, [page]);

  useEffect(() => {
    sessionStorage.setItem(
      "messagesRestoreState",
      JSON.stringify({
        selectedFilter: filter,
        selectedPage: page,
        selectedPageSize: pageSize,
      })
    );
  }, [filter, page, pageSize]);

  useEffect(() => {
    handleFetchFilterCounts();
  }, []);

  useEffect(() => {
    handleFetchconversations();
  }, [filter, page, pageSize]);

  const handleFetchFilterCounts = () => {
    showSpinner();
    const fetch_filter_count_done = (resStatus, responseString, resJson) => {
      closeSpinner();
      if (resStatus == 200) {
        setFilterCount({
          unread: resJson?.unread?.count,
          disputes: resJson?.disputes?.count,
          flagged: resJson?.flagged?.count,
          flaggedByAI: resJson?.flaggedByAI?.count,
        });
      } else if (resStatus == 400) {
        modal(responseString);
      } else if (resStatus !== 200) {
        modal(responseString, "Something went wrong!");
      }
    };

    const queryParams = {
      page: page,
      limit: pageSize,
    };
    getMessagesFilterCounts(
      queryParams,
      fetch_filter_count_done,
      userDetails?.jwttoken
    );
  };

  const handleFetchconversations = async () => {
    showSpinner();

    const conversations_done = (resStatus, responseString, resJson) => {
      closeSpinner();
      if (resStatus == 200) {
        setMessagesData(resJson);
        setTotalPages(
          Math.ceil(
            (filter === "flaggedByAI"
              ? resJson?.totalMessages
              : resJson?.totalParentRecords) / pageSize
          )
        );
      } else if (resStatus == 400) {
        modal(responseString);
      } else if (resStatus !== 200) {
        modal(responseString, "Something went wrong!");
      }
    };

    const queryParams = {
      messageType: filter,
      search: searchValue,
      page: page,
      limit: pageSize,
    };
    //get messages api call
    conversations(queryParams, conversations_done, userDetails.jwttoken);
  };

  const handleFetchunSuspendUser = async ({
    userId,
    userName,
    offenseDetailId,
    decision,
    userType,
    reason,
  }) => {
    showSpinner();

    const unSuspendUser_done = (resStatus, responseString, resJson) => {
      closeSpinner();
      if (resStatus == 200) {
        handleFetchconversations();
        const statusText = decision === "suspended" ? "Suspended" : "Unsuspended";
        setSuspensionModalData({
          userName,
          userType,
          statusText,
          reason: reason || "No reason provided",
          message: resJson?.message,
          decision,
        });
      } else if (resStatus == 400) {
        modal(responseString);
      } else if (resStatus !== 200) {
        modal(responseString, "Something went wrong!");
      }
    };
    const payload = {
      userId,
      offenseDetailId,
      decision,
      userType,
      reason,
    };
    unsuspenduser(payload, unSuspendUser_done, userDetails.jwttoken);
  };

  const handleBulkReadMessages = async () => {
    showSpinner();

    const requestBody = {
      conversations: selectedCheckbox?.map((item) => ({
        _id: item?.conversationId,
        messageId: item?.messageId,
      })),
    };

    try {
      bulkread(
        requestBody,
        (resStatus, responseString, resJson) => {
          if (resStatus === 200) {
            closeSpinner();
            setSelectedCheckbox([]);
            handleFetchFilterCounts();
            handleFetchconversations();
            modal("✅ Selection marked as read successfully!", "Good Job");
          } else {
            closeSpinner();
            modal(
              responseString,
              resStatus === 400 ? "Bad Request" : "Something went wrong!"
            );
          }
        },
        userDetails.jwttoken
      );
    } catch (error) {
      closeSpinner();
    }
  };

  const handleCheckboxChange = (messageDetails) => {
    const newCheckbox = {
      conversationId: messageDetails?._id,
      messageId: getMessageData(messageDetails)?._id,
    };

    setSelectedCheckbox((prevSelected) => {
      const exists = prevSelected?.some(
        (msg) =>
          msg?.conversationId === newCheckbox?.conversationId &&
          msg?.messageId === newCheckbox?.messageId
      );

      if (exists) {
        return prevSelected?.filter(
          (msg) =>
            msg?.conversationId !== newCheckbox?.conversationId ||
            msg?.messageId !== newCheckbox?.messageId
        );
      } else {
        return [...prevSelected, newCheckbox];
      }
    });
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allSelectMessages = messagesData?.data?.map((item) => ({
        conversationId: item?._id,
        messageId: getMessageData(item)?._id,
      }));
      setSelectedCheckbox(allSelectMessages);
    } else {
      setSelectedCheckbox([]);
    }
  };

  const handleFilterInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setFilter(value);
    setSelectedCheckbox([]);
    setPage(1);
  };

  const getUserIcon = (userType) => {
    switch (userType) {
      case "admin":
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.5035 10.1314L13.8591 8.77753C13.8955 8.51291 13.9131 8.25609 13.9131 8C13.9131 7.74391 13.8955 7.48709 13.8591 7.22247L15.5035 5.86853C15.7005 5.70616 15.7514 5.42559 15.6237 5.20481L14.2324 2.79519C14.105 2.57406 13.8367 2.47759 13.5976 2.56759L11.5993 3.31622C11.1883 2.99963 10.7386 2.74013 10.2569 2.54109L9.90599 0.436125C9.86421 0.184438 9.64649 0 9.3914 0H6.60881C6.35371 0 6.13596 0.184438 6.09421 0.436125L5.74334 2.54109C5.26168 2.74016 4.81196 2.99966 4.40093 3.31622L2.40259 2.56759C2.16381 2.47722 1.89512 2.57406 1.76774 2.79519L0.376431 5.20481C0.248713 5.42562 0.299681 5.70616 0.496681 5.86853L2.14103 7.22247C2.10471 7.48709 2.08706 7.74391 2.08706 8C2.08706 8.25609 2.10471 8.51291 2.14109 8.77753L0.496744 10.1315C0.299744 10.2938 0.248775 10.5744 0.376494 10.7952L1.76781 13.2048C1.89553 13.4259 2.16418 13.5231 2.40265 13.4324L4.40093 12.6838C4.81193 13.0004 5.26165 13.2599 5.74334 13.4589L6.09421 15.5639C6.13596 15.8155 6.35368 16 6.60878 16C7.63406 16 8.36518 16 9.39137 16C9.64646 16 9.86421 15.8155 9.90599 15.5638L10.2569 13.4589C10.7385 13.2598 11.1882 13.0003 11.5993 12.6838L13.5976 13.4324C13.8363 13.5227 14.105 13.4259 14.2324 13.2048L15.6237 10.7952C15.7515 10.5744 15.7005 10.2938 15.5035 10.1314ZM9.04356 14.9565H6.95659V12.0359C7.29756 12.1248 7.64649 12.1729 8.00006 12.1729C8.35571 12.1729 8.70031 12.1262 9.04353 12.0358L9.04356 14.9565ZM9.15802 10.9059C9.14684 10.9103 9.13424 10.9144 9.12203 10.9182C8.74499 11.0618 8.37543 11.1294 8.00009 11.1294C7.61149 11.1294 7.22937 11.0568 6.86421 10.9141C6.85674 10.9117 6.84924 10.909 6.84212 10.9063C5.64374 10.427 4.86962 9.28634 4.86962 8.00003C4.86962 7.04181 5.29762 6.1515 6.04421 5.55744C6.27146 5.37684 6.60874 5.53806 6.60874 5.8295V7.82613C6.60874 7.88828 6.64237 7.94637 6.6964 7.97728L7.91381 8.67294C7.93996 8.68791 7.96984 8.69569 8.00006 8.69569C8.03027 8.69569 8.06018 8.68787 8.08631 8.67294L9.30368 7.97728C9.35771 7.94634 9.39134 7.88828 9.39134 7.82613V5.82953C9.39134 5.53844 9.7289 5.37713 9.95587 5.55747C10.7025 6.15153 11.1305 7.04181 11.1305 8.00003C11.1305 9.28634 10.3564 10.427 9.15802 10.9059Z"
              fill="#5A5A5A"
            />
          </svg>
        );
      case "mav":
        return (
          <svg
            width="16"
            height="12"
            viewBox="0 0 16 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.1841 2.60813C12.9921 2.35213 12.6081 2.22413 12.2881 2.22413H10.7521V1.64813C10.7521 1.00813 10.2401 0.432129 9.53607 0.432129H1.53607C0.896068 0.432129 0.320068 0.944129 0.320068 1.64813V8.30413C0.320068 8.94413 0.832068 9.52013 1.53607 9.52013H2.04807C2.17607 10.6721 3.13607 11.5681 4.28807 11.5681C5.44007 11.5681 6.46407 10.6721 6.52807 9.52013H9.79207C9.92007 10.6721 10.8801 11.5681 12.0321 11.5681C13.1841 11.5681 14.2081 10.6721 14.2721 9.52013H14.4641C15.1041 9.52013 15.6801 9.00813 15.6801 8.30413V5.87213C15.6801 5.55213 15.5521 5.29613 15.3601 5.04013L13.1841 2.60813ZM4.28807 10.2881C3.77607 10.2881 3.32807 9.84013 3.32807 9.32813C3.32807 8.81613 3.77607 8.36813 4.28807 8.36813C4.80007 8.36813 5.24807 8.81613 5.24807 9.32813C5.24807 9.84013 4.80007 10.2881 4.28807 10.2881ZM12.0321 10.2881C11.5201 10.2881 11.0721 9.84013 11.0721 9.32813C11.0721 8.81613 11.5201 8.36813 12.0321 8.36813C12.5441 8.36813 12.9921 8.81613 12.9921 9.32813C13.0561 9.84013 12.6081 10.2881 12.0321 10.2881ZM13.9521 5.87213H11.0081C10.8801 5.87213 10.8161 5.80813 10.8161 5.68013V3.69613C10.8161 3.56813 10.8801 3.50413 11.0081 3.50413H12.1601C12.2241 3.50413 12.2881 3.50413 12.2881 3.56813L14.0801 5.55213C14.2081 5.68013 14.0801 5.87213 13.9521 5.87213Z"
              fill="currentColor"
            />
          </svg>
        );
      case "user":
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.33 8.18516C9.93676 7.88506 10.4242 7.38857 10.7131 6.77639C11.002 6.16421 11.0753 5.47231 10.9212 4.81316C10.7671 4.15401 10.3946 3.56635 9.8643 3.14569C9.33395 2.72503 8.67692 2.49609 8 2.49609C7.32308 2.49609 6.66605 2.72503 6.1357 3.14569C5.60536 3.56635 5.23286 4.15401 5.07877 4.81316C4.92467 5.47231 4.99803 6.16421 5.28691 6.77639C5.5758 7.38857 6.06324 7.88506 6.67 8.18516C5.61593 8.47603 4.68626 9.10441 4.02341 9.97407C3.36056 10.8437 3.00106 11.9067 3 13.0002V13.5002C3 13.6328 3.05268 13.7599 3.14645 13.8537C3.24021 13.9475 3.36739 14.0002 3.5 14.0002H12.5C12.6326 14.0002 12.7598 13.9475 12.8536 13.8537C12.9473 13.7599 13 13.6328 13 13.5002V13.0002C12.9989 11.9067 12.6394 10.8437 11.9766 9.97407C11.3137 9.10441 10.3841 8.47603 9.33 8.18516Z"
              fill="currentColor"
            />
          </svg>
        );
      default:
        null;
    }
  };

  const ConversationHeader = ({ participants, message }) => {
    if (!participants || participants.length < 2) return null;

    // Use the normalized message data
    const messageData = getMessageData(message);

    const messageSenderParticipant = participants.find(
      (participant) => participant?.user === messageData?.user
    );

    const otherParticipant = participants.find(
      (participant) => participant?.user !== messageData?.user
    );

    let firstParticipant, secondParticipant;

    if (messageSenderParticipant) {
      firstParticipant = messageSenderParticipant;
      secondParticipant = otherParticipant;
    } else {
      firstParticipant = participants[0];
      secondParticipant = participants[1];
    }

    const renderUser = (participant) => {
      if (!participant) return null;

      const userPath =
        participant?.userType === userType.CUSTOMER
          ? `/client/${participant?.user_short_id}`
          : `/${participant?.userType}/${participant?.user_short_id}`;

      if (participant.userType !== "admin") {
        return (
          <>
            {participant.accountInfo?.username && (
              <a
                href="#"
                className="font-semibold"
                onClick={(e) => toScreen(e, navigate, userPath)}
              >
                {participant.accountInfo?.username}
              </a>
            )}
            {!participant.accountInfo?.username && (
              <span className="font-semibold text-gray">Unknown</span>
            )}
          </>
        );
      } else {
        return <span className="font-semibold text-gray">Admin</span>;
      }
    };

    return (
      <span className="color-teal g-4">
        {getUserIcon(firstParticipant?.userType || "admin")}
        {renderUser(firstParticipant)}

        <span>
          <svg
            width="15"
            height="10"
            viewBox="0 0 15 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.5"
              d="M0.688 5.784V4.12H4.624V5.784H0.688ZM10.3845 9.272L9.2645 8.008L11.6645 5.896H5.9525V4.04H11.6645L9.2645 1.928L10.3845 0.664L14.8325 4.936V5L10.3845 9.272Z"
              fill="#5A5A5A"
            />
          </svg>
        </span>

        {getUserIcon(secondParticipant?.userType || "admin")}
        {renderUser(secondParticipant)}
      </span>
    );
  };

  const handleViewConversation = (event, messageDetails) => {
    const participants = messageDetails?.participants;

    // Identify participants
    const admin = participants?.find(
      (item) => item?.userType === "admin" || (!item?.userType && "admin")
    );
    const mav = participants.find((item) => item?.userType === "mav");
    const user = participants.find((item) => item?.userType === "user");
    // Determine conversation URL
    let conversationUrl = "";

    if (admin) {
      conversationUrl = `/message/${mav?.user_short_id || user?.user_short_id}`;
    } else if (mav && user) {
      conversationUrl = `/message/${mav?.user_short_id}/${user?.user_short_id}`;
    }

    //for use getConversation api params and payload
    let receiver = participants?.find(
      (item) => item?.user_short_id !== "Admin"
    );
    let adminUser = participants?.find(
      (item) => item?.user_short_id === "Admin"
    );
    const receiverShortId = receiver ? receiver?.user_short_id : null;
    const receiverId = receiver ? receiver?.user : null;

    let userShortId1 = adminUser
      ? adminUser?.user_short_id
      : receiverShortId === participants[0]?.user_short_id
        ? participants[1]?.user_short_id
        : participants[0]?.user_short_id;

    // Get user type from the normalized message data
    let userShortId2 = getMessageData(messageDetails)?.userType;

    const suspendedUser = messageDetails?.participants?.find(
      (p) => p?.user === messageDetails?.userId
    );
    const suspendedUserName = suspendedUser?.accountInfo?.username || messageDetails?.userId;

    const suspendData =
      messageDetails?.isSuspended !== null &&
      messageDetails?.isSuspended !== undefined
        ? {
            isSuspended: messageDetails?.isSuspended,
            userId: messageDetails?.userId,
            userName: suspendedUserName,
            offenseDetailId: messageDetails?._id,
            userType: messageDetails?.userType,
            reason: messageDetails?.message?.flaggedByAI?.suspiciousAction,
          }
        : null;

    const extraAttributes = {
      receiverId,
      receiverShortId,
      receiverType: "",
      userShortId1:
        userShortId1 === "Admin" ? userShortId1?.toLowerCase() : userShortId1,
      userShortId2,
      username2: "",
      selectedPage: page,
      selectedPageSize: pageSize,
      selectedFilter: filter,
      previousPath: window.location.pathname,
      conversation: [],
      suspendData,
    };

    // navigate on message page with extra params
    toScreen(event, navigate, conversationUrl, false, extraAttributes);
  };

  const getFilterLabel = (filter) => {
    switch (filter) {
      case "all":
        return "All Messages";
      case "admin":
        return "Admin / User";
      case "unread":
        return "Admin / Unread";
      case "disputes":
        return "Admin / Disputes";
      case "user":
        return "User / User";
      case "flagged":
        return "User / Flagged";
      case "flaggedByAI":
        return "User / Flagged By AI";
      default:
        return "Messages";
    }
  };

  return (
    <div className="l-base-layout__content">
      {suspensionModalData && (
        <SuspensionModal
          userName={suspensionModalData.userName}
          userType={suspensionModalData.userType}
          statusText={suspensionModalData.statusText}
          reason={suspensionModalData.reason}
          message={suspensionModalData.message}
          decision={suspensionModalData.decision}
          onClose={() => setSuspensionModalData(null)}
        />
      )}
      <h1>Admin / Messages</h1>

      <div className="c-hr"></div>
      <div className="l-row">
        <div className="l-col-33">
          <div className="c-checkbox-radio-block">
            <input
              type="radio"
              id="all"
              name="filter"
              value="all"
              onChange={handleFilterInputChange}
              checked={filter === "all"}
            />
            <label htmlFor="all">All Messages</label>
          </div>
          <div className="c-checkbox-radio-block">
            <input
              type="radio"
              id="admin"
              name="filter"
              value="admin"
              onChange={handleFilterInputChange}
              checked={filter === "admin"}
            />
            <label htmlFor="admin">Admin / User</label>
          </div>
          <div className="c-checkbox-radio-block">
            <input
              type="radio"
              id="unread"
              name="filter"
              value="unread"
              onChange={handleFilterInputChange}
              checked={filter === "unread"}
            />
            <label htmlFor="unread">
              — Unread {`(${filterCount?.unread ?? 0})`}
            </label>
          </div>
          <div className="c-checkbox-radio-block">
            <input
              type="radio"
              id="disputes"
              name="filter"
              value="disputes"
              onChange={handleFilterInputChange}
              checked={filter === "disputes"}
            />
            <label htmlFor="disputes">
              — Disputes {`(${filterCount?.disputes ?? 0})`}
            </label>
          </div>
          <div className="c-checkbox-radio-block">
            <input
              type="radio"
              id="user"
              name="filter"
              value="user"
              onChange={handleFilterInputChange}
              checked={filter === "user"}
            />
            <label htmlFor="user">User / User</label>
          </div>
          <div className="c-checkbox-radio-block">
            <input
              type="radio"
              id="flagged"
              name="filter"
              value="flagged"
              onChange={handleFilterInputChange}
              checked={filter === "flagged"}
            />
            <label htmlFor="flagged">
              — Flagged {`(${filterCount?.flagged ?? 0})`}
            </label>
          </div>
          <div className="c-checkbox-radio-block">
            <input
              type="radio"
              id="flaggedByAI"
              name="filter"
              value="flaggedByAI"
              onChange={handleFilterInputChange}
              checked={filter === "flaggedByAI"}
            />
            <label htmlFor="flaggedByAI">
              — Flagged By AI {`(${filterCount?.flaggedByAI ?? 0})`}
            </label>
          </div>
        </div>

        <div className="l-col-66">
          <div className="c-subheader-info-panel">
            <div className="c-subheader-info-panel__primary">
              {`Showing: ${getFilterLabel(filter)} (${messagesData?.totalMessages ?? 0
                })`}
            </div>
            <div className="c-subheader-info-panel__search">
              <form>
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                  }}
                  name="search"
                  placeholder="Search by shortId, first & last name, email"
                  className="c-subheader-info-panel__input"
                />
                <button
                  type="submit"
                  className="c-btn-outline c-btn-outline--white c-btn-outline--small"
                  onClick={(e) => {
                    e.preventDefault();
                    handleFetchconversations();
                    setPage(1);
                  }}
                >
                  Search
                </button>
              </form>
            </div>
          </div>
          {messagesData?.data?.length > 0 &&
            (filter === "admin" ||
              filter === "disputes" ||
              filter === "unread") && (
              <div className="bulk-read-section">
                <form>
                  <div className="select-all-checkbox">
                    <input
                      type="checkbox"
                      id="select-all"
                      name="select-all"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={
                        selectedCheckbox?.length === messagesData?.data?.length
                      }
                    />
                    <label htmlFor="select-all">Select all</label>
                  </div>
                </form>

                <span>
                  <button
                    type="button"
                    className={
                      selectedCheckbox?.length > 0
                        ? "btn-bulk-read-after"
                        : "btn-bulk-read-before"
                    }
                    onClick={() => handleBulkReadMessages()}
                    disabled={selectedCheckbox?.length <= 0}
                  >
                    {selectedCheckbox?.length > 0
                      ? "Mark as read"
                      : "Mark selection as read"}
                  </button>
                </span>
              </div>
            )}

          <div className="c-hr u-margin-bottom-0"></div>
          <div className="messages-list-section">
            {messagesData?.data?.length > 0
              ? messagesData?.data?.map((messageDetails, index) => {
                // Get normalized message data
                const messageData = getMessageData(messageDetails);

                const date = moment(messageData?.standardDate).format(
                  "DD MMM YYYY"
                );

                const time = moment(messageData?.standardDate).format(
                  "HH:mm"
                );

                const jobShortId = messageData?.associatedJobShortID;

                const isAdminReadStatus =
                  messageData?.userType !== "admin" &&
                  messageData?.readDetailsAdmin?.status === "unread";

                const isCheckedMsg = selectedCheckbox?.some(
                  (msg) =>
                    msg?.conversationId === messageDetails?._id &&
                    msg?.messageId === messageData?._id
                );

                return (
                  <div
                    className={`msg-wrapper ${isCheckedMsg && "selected-msg-wrapper"
                      }`}
                    key={index}
                  >
                    <div className="msg-outer">
                      <div className="msg-header">
                        <div className="msg-header-left">
                          {(filter === "admin" ||
                            filter === "disputes" ||
                            filter === "unread") && (
                              <form>
                                <div className="select-all-checkbox">
                                  <input
                                    type="checkbox"
                                    id={`checkbox-${messageDetails._id}`}
                                    name="checkbox"
                                    onChange={() =>
                                      handleCheckboxChange(messageDetails)
                                    }
                                    checked={isCheckedMsg}
                                  />
                                  <label
                                    htmlFor={`checkbox-${messageDetails._id}`}
                                  ></label>
                                </div>
                              </form>
                            )}
                          {ConversationHeader({
                            participants: messageDetails.participants,
                            message: messageDetails,
                          })}
                          <div className="d-flex ml-32">
                            <span className="u-before-icon-calendar2"></span>
                            &nbsp;
                            <span>{date}</span>&nbsp;&nbsp;&nbsp;
                            <span className="u-before-icon-clock"></span>
                            &nbsp;
                            <span>{time}</span>{" "}
                          </div>
                        </div>
                        <div className="msg-header-right">
                          {jobShortId && (
                            <p>
                              Job:{" "}
                              <a
                                href="#"
                                className="color-teal font-semibold u-uppercase"
                                onClick={(e) =>
                                  toScreen(e, navigate, "/job/" + jobShortId)
                                }
                              >
                                {jobShortId}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                      {filter !== "flaggedByAI" && (
                        <div
                          className={`msg-body ${(filter === "admin" ||
                            filter === "unread" ||
                            filter === "disputes") &&
                            "ml-32"
                            } ${isAdminReadStatus && "unread-msg-body"}`}
                        >
                          <p>{messageDetails?.message?.message}</p>
                        </div>
                      )}

                      {filter === "flaggedByAI" && (
                        <div className="msg-body-wrapper">
                          <div className="msg-body">
                            <p>{messageDetails?.message?.message}</p>
                          </div>
                          {messageDetails?.reason && (
                            <div className="flagged-wrapper">
                              <span className="flagged-text">
                                <strong>Flagged due to : </strong>&nbsp;
                                {messageDetails?.reason}
                              </span>
                              {messageDetails?.isSuspended !== null
                                && (
                                  <button
                                    className={`c-btn c-btn--3 c-btn--small ${messageDetails?.isSuspended
                                      ? "c-btn--teal"
                                      : "c-btn--red"
                                      }`}
                                    onClick={() => {
                                      const suspendedUser = messageDetails?.participants?.find(
                                        (p) => p?.user === messageDetails?.userId
                                      );
                                      const userName = suspendedUser?.accountInfo?.username || messageDetails?.userId;
                                      handleFetchunSuspendUser({
                                        userId: messageDetails?.userId,
                                        userName,
                                        offenseDetailId: messageDetails?._id,
                                        userType: messageDetails?.userType,
                                        decision: messageDetails?.isSuspended
                                          ? "unsuspended"
                                          : "suspended",
                                        reason:
                                          messageDetails?.message?.flaggedByAI
                                            ?.suspiciousAction,
                                      });
                                    }}
                                  >
                                    {messageDetails?.isSuspended
                                      ? "Undo Suspension"
                                      : "Suspend"}
                                  </button>
                                )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="view-btn">
                      <button
                        type="button"
                        className={`c-btn c-btn--3 c-btn--small ${isAdminReadStatus ? "c-btn--green" : "c-btn--black"
                          }`}
                        onClick={(e) =>
                          handleViewConversation(e, messageDetails)
                        }
                      >
                        View Conversation
                      </button>
                    </div>
                  </div>
                );
              })
              : messagesData?.data?.length <= 0 && (
                <p className="text-danger mt-16">No messages found</p>
              )}
          </div>
          <div className="c-hr u-margin-bottom-0"></div>
          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </div>
  );
};

export default Messages;
